import React, { useState } from "react";
import { useNavigate } from "react-router";

import { LoadingView } from "../../components/loading-view/LoadingView";
import { ErrorView } from "../../components/error-view/ErrorView";
import { usePageQuery } from "../../shared/hooks/usePageQuery";
import {
  PagedComponentState,
  usePagedComponentStateMachine,
} from "../../shared/hooks/usePageComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { useCustomerAndVendors } from "../../shared/hooks/useCustomerAndVendors";
import { OrderListWrapper } from "./wrappers/order-list.wrapper";
import { Order } from "../../../core/models/entities/Order";
import { useDialog } from "../../components/Dialogs/dialog-base";
import { RejectionNoteDialog } from "../../components/Dialogs/rejection-note-popup.component";
import { OrderTitleHeader } from "../../components/Logo/LogoTitleHeader";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { useCartHidden } from "../../../hooks/useCartHidden";

export function OrderListScreen(): React.ReactElement {
  const navigate = useNavigate();
  const [query, queryChanger] = usePageQuery(25);
  const { state, dataTransition } = usePagedComponentStateMachine();
  const { orderRepository, organizationRepository } = useRepositoriesContext();
  const [lastPage, setLastPage] = useState<number>(1);
  const [orderRejection, setOrderRejection] = useState<Order | null>(null);
  const { profile, role } = useProfileContext();
  const rejectionNoteDialog = useDialog();

  function showRejectionNote(order: Order): void {
    setOrderRejection(order);
    rejectionNoteDialog.openDialog();
  }

  const {
    data: ordersPage,
    isSuccess: isOrdersSuccess,
    isLoading: isOrdersLoading,
    isError: isOrdersError,
  } = orderRepository.useAllOrders(query);

  const {
    customers,
    vendors,
    isSuccess: isOrganizationSuccess,
    isError: isOrganizationError,
    isLoading: isOrganizationLoading,
  } = useCustomerAndVendors(organizationRepository);

  if (ordersPage && ordersPage.pageCount !== lastPage) {
    setLastPage(ordersPage.pageCount);
  }

  // Hidden-price customers keep the list, but row-click leads to order/confirm
  // (prices) - make it a no-op instead of bouncing through the route guard.
  const cartHidden = useCartHidden();
  const navigateOrder = (order: Order) => {
    if (cartHidden) return;
    navigate(
      `/order/confirm?orderId=${order.id}&orgId=${order.organization.id}`
    );
  };

  const isLoading = isOrdersLoading || isOrganizationLoading;
  const isSuccess = isOrdersSuccess && isOrganizationSuccess;
  const isError = isOrdersError || isOrganizationError;
  dataTransition(isLoading, isSuccess, isError);

  return state === PagedComponentState.Succeed ||
    state === PagedComponentState.PageLoading ? (
    <>
      <OrderListWrapper
        profile={profile!}
        query={query}
        queryChanger={queryChanger}
        isPageLoading={state === PagedComponentState.PageLoading}
        lastPage={lastPage}
        orders={ordersPage?.results}
        customers={customers!}
        vendors={vendors}
        onSelectOrder={navigateOrder}
        onRejectionNote={showRejectionNote}
      />
      {orderRejection && (
        <RejectionNoteDialog
          title={<OrderTitleHeader order={orderRejection!} />}
          dialogOptions={rejectionNoteDialog}
          approval={orderRejection.approval!}
          exclude={role!}
          isOrder={true} // This is for an order, so hide edit button
        />
      )}
    </>
  ) : state === PagedComponentState.Failed ? (
    <ErrorView />
  ) : (
    <LoadingView />
  );
}
